import { useEffect, useMemo, useState } from 'react';
import { Card, PageTitle } from '../ui';
import { Copy, Users, Mic2, Merge, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';
import { useConfirm } from '../components/ConfirmDialog';

type Resource = 'clients' | 'artistes';

interface Row {
  id: string;
  label: string;
  email?: string;
  tel?: string;
  siret?: string;
  ville?: string;
  raw: any;
}

interface Group {
  key: string;
  reason: string;
  rows: Row[];
}

const norm = (s?: string | null) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '').trim();

const normPhone = (s?: string | null) => (s || '').replace(/\D/g, '').replace(/^33/, '0').slice(-10);

function detect(rows: Row[]): Group[] {
  const groups: Record<string, Group> = {};
  const add = (key: string, reason: string, r: Row) => {
    if (!key) return;
    const k = `${reason}::${key}`;
    if (!groups[k]) groups[k] = { key: k, reason, rows: [] };
    if (!groups[k].rows.find((x) => x.id === r.id)) groups[k].rows.push(r);
  };
  for (const r of rows) {
    add(norm(r.label), 'Nom identique', r);
    if (r.email) add(norm(r.email), 'Email identique', r);
    if (r.tel) add(normPhone(r.tel), 'Téléphone identique', r);
    if (r.siret) add(norm(r.siret), 'SIRET identique', r);
  }
  return Object.values(groups).filter((g) => g.rows.length >= 2);
}

export default function Doublons() {
  const [tab, setTab] = useState<Resource>('clients');
  const confirm = useConfirm();
  const [rows, setRows] = useState<Record<Resource, Row[]>>({ clients: [], artistes: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        supabase.from('clients').select('id,nom,prenom,societe,email,tel,siret,ville').is('deleted_at', null),
        supabase.from('artistes').select('id,nom_scene,nom,prenom,email,tel,siret,ville').is('deleted_at', null),
      ]);
      setRows({
        clients: (c.data || []).map((r: any) => ({
          id: r.id,
          label: r.societe || [r.prenom, r.nom].filter(Boolean).join(' ') || r.nom || '—',
          email: r.email, tel: r.tel, siret: r.siret, ville: r.ville, raw: r,
        })),
        artistes: (a.data || []).map((r: any) => ({
          id: r.id,
          label: r.nom_scene || [r.prenom, r.nom].filter(Boolean).join(' ') || '—',
          email: r.email, tel: r.tel, siret: r.siret, ville: r.ville, raw: r,
        })),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => detect(rows[tab]), [rows, tab]);

  const merge = async (group: Group, masterId: string) => {
    const master = group.rows.find((r) => r.id === masterId)!;
    const others = group.rows.filter((r) => r.id !== masterId);
    const ok = await confirm({
      title: `Fusionner ${others.length} fiche(s) ?`,
      description:
        `Maître : « ${master.label} ».\nLes fiches doublons seront mises à la corbeille.\n` +
        (tab === 'clients'
          ? `Les prestations / devis / factures liés seront réattribués à la fiche maître.`
          : `Les liens prestations_artistes seront réattribués à l'artiste maître.`),
      confirmLabel: 'Fusionner',
    });
    if (!ok) return;

    setBusy(group.key);
    try {
      for (const dup of others) {
        if (tab === 'clients') {
          // Réattribuer toutes les références
          await Promise.all([
            supabase.from('prestations').update({ client_id: master.id }).eq('client_id', dup.id),
            supabase.from('devis').update({ client_id: master.id }).eq('client_id', dup.id),
            supabase.from('factures').update({ client_id: master.id }).eq('client_id', dup.id),
          ]);
          await supabase.from('clients').update({ deleted_at: new Date().toISOString() }).eq('id', dup.id);
        } else {
          await supabase.from('prestations_artistes').update({ artiste_id: master.id, artiste_nom: master.label }).eq('artiste_id', dup.id);
          await supabase.from('artistes').update({ deleted_at: new Date().toISOString() }).eq('id', dup.id);
        }
      }
      toast.success(`Fusion réussie (${others.length} fiche${others.length > 1 ? 's' : ''} archivée${others.length > 1 ? 's' : ''})`);
      await load();
    } catch (e: any) {
      toast.error('Échec de la fusion : ' + translateError(e.message));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageTitle
        title="Détection de doublons"
        subtitle="Identifie les fiches en doublon et fusionne-les en gardant la fiche maître"
        icon={<Copy className="text-amber-400" size={24} strokeWidth={1.5} />}
      />

      <div className="flex gap-2 mb-4">
        {(['clients', 'artistes'] as Resource[]).map((r) => (
          <button
            key={r}
            onClick={() => setTab(r)}
            className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
              tab === r ? 'bg-amber-500 text-slate-950 font-medium' : 'bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800'
            }`}
          >
            {r === 'clients' ? <Users size={14} /> : <Mic2 size={14} />}
            {r === 'clients' ? 'Clients' : 'Artistes'}
          </button>
        ))}
      </div>

      <Card>
        <div className="px-4 py-3 border-b border-slate-800 text-sm text-slate-300">
          {loading ? 'Analyse…' : `${groups.length} groupe(s) de doublons détecté(s) sur ${rows[tab].length} fiche(s)`}
        </div>

        {!loading && groups.length === 0 && (
          <div className="px-4 py-12 text-center text-slate-500 text-sm">
            ✓ Aucun doublon détecté. Vos {tab} sont propres.
          </div>
        )}

        <div className="divide-y divide-slate-800">
          {groups.map((g) => (
            <div key={g.key} className="px-4 py-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  {g.reason}
                </span>
                <span className="text-xs text-slate-500">{g.rows.length} fiches</span>
              </div>
              <div className="space-y-2">
                {g.rows.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-900/60 rounded border border-slate-800">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-200 font-medium truncate">{r.label}</div>
                      <div className="text-[11px] text-slate-500 flex gap-3 mt-0.5">
                        {r.email && <span>📧 {r.email}</span>}
                        {r.tel && <span>📞 {r.tel}</span>}
                        {r.siret && <span>SIRET {r.siret}</span>}
                        {r.ville && <span>📍 {r.ville}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => merge(g, r.id)}
                      disabled={busy === g.key}
                      className="px-3 py-1.5 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded flex items-center gap-1.5 disabled:opacity-50"
                      title="Garder cette fiche comme maître et fusionner les autres dedans"
                    >
                      <Merge size={12} /> Garder & fusionner
                      <ChevronRight size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
